import { AnalyticsEvent, logAnalyticsEvent } from 'utils/analytics'
import { FC, useEffect } from 'react'
import { GRAY_700, YELLOW_100 } from 'constants/styling/theme'
import { formatReopensAt, isSupportClosedAt } from '../helpers/supportHours'

import { ConversationType } from 'models/conversations'
import ScheduleOutlinedIcon from '@mui/icons-material/ScheduleOutlined'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { logger } from 'utils/logging'
import { useGetSupportHours } from 'dataQueries/conversations.query'
import { useSupportHoursLocale } from './useSupportHoursLocale'
import { useSupportHoursNow } from './useSupportHoursNow'
import { useTranslation } from 'react-i18next'

export type SupportClosedNoticeSurface = 'composer' | 'thread'

interface Props {
  surface: SupportClosedNoticeSurface
  conversationId?: string
  conversationType?: ConversationType
}

const reportedNotices = new Set<string>()
const reportedUnreadableOpenings = new Set<string>()

export const SupportClosedNotice: FC<Props> = ({ surface, conversationId, conversationType }) => {
  const { t } = useTranslation(['chat'])
  const locale = useSupportHoursLocale()
  const { data: hours } = useGetSupportHours()
  const now = useSupportHoursNow(hours)
  const isClosed = isSupportClosedAt(hours, now)
  const reopensAt = hours && isClosed ? formatReopensAt(hours.nextOpeningAt, locale) : null
  const nextOpeningAt = hours?.nextOpeningAt

  useEffect(() => {
    if (!isClosed || nextOpeningAt === undefined) return
    const noticeKey = `${surface}:${conversationId ?? ''}:${nextOpeningAt}`
    if (!reportedNotices.has(noticeKey)) {
      reportedNotices.add(noticeKey)
      logAnalyticsEvent(AnalyticsEvent.CHAT_WIDGET_SUPPORT_CLOSED_NOTICE_SHOWN, {
        surface,
        conversationId: conversationId ?? null,
        conversationType: conversationType ?? null,
        withReopeningTime: !!reopensAt,
      })
    }
    if (!reopensAt && !reportedUnreadableOpenings.has(nextOpeningAt)) {
      reportedUnreadableOpenings.add(nextOpeningAt)
      logger.warn('ChatWidget: next support opening is unreadable, showing the closed notice without a time', undefined, {
        context: { component: 'SupportClosedNotice', action: 'formatReopensAt', metadata: { nextOpeningAt } },
      })
    }
  }, [isClosed, nextOpeningAt, reopensAt, surface, conversationId, conversationType])

  if (!hours || !isClosed) return null

  return (
    <Stack
      direction="row"
      gap="0.8rem"
      alignItems="flex-start"
      role="status"
      data-testid="support-closed-notice"
      sx={{ padding: '0.8rem 1.6rem', backgroundColor: YELLOW_100 }}
    >

      <ScheduleOutlinedIcon sx={{ fontSize: '1.6rem', color: GRAY_700, marginTop: '0.1rem' }} />

      <Typography variant="text-xs" color={GRAY_700}>
        {reopensAt
          ? t('chat:support_hours.closed_notice', { reopensAt })
          : t('chat:support_hours.closed_notice_no_time')}
      </Typography>

    </Stack>
  )
}
